
import { ActionOrTriggerListTypeEnum, IActionOrTriggerListPageParams } from "@app/static";
import { getProxyDispatcher, useProxyWatch, useTranslation } from "@fanfanlo";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, FormControlLabel, Grid2, Stack, Switch, TextField } from "@mui/material";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { a11yNodeProperties, a11yNodeRealizedKeys, IA11yNode, IA11yNodeInfo, NodeAction } from "../../../a11y/A11yNode";
import { NodeActionUI } from "../../../a11y/NodeActionUI";
import { childToProjectDataUtils, IChildToProjectData } from "../../../utils";
import { actionA11yNodeExecuteUtils, IActionA11yNodeExecute } from "./node-execute";

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useProjectUniqueKeyValue } from "../../../common";
import { parseInt } from "lodash";


function SelectConditions({ data }: { data: IActionA11yNodeExecute }) {
    const node = data.javaData.info?.node
    if (!node) {
        return <></>
    }
    
    const { t } = useTranslation("droid-project-interface/actions/a11y/node-execute/content")
    const [showAll, setShowAll] = useProjectUniqueKeyValue<boolean>((projectInfo) => {
        childToProjectDataUtils.actionList.getActionListByProp(data, projectInfo)
        projectInfo.action = data
    }, 'show-all-properties', false)
    const [keys, setKeys] = useState(showAll ? Object.keys(a11yNodeProperties) : a11yNodeRealizedKeys)
    useEffect(() => {
        setKeys(showAll ? Object.keys(a11yNodeProperties) : a11yNodeRealizedKeys)
    }, [showAll])
    return (
        <>
            <Box sx={{ width: "100%" }}>
                <Box>

                    <FormControlLabel control={<Switch checked={Boolean(showAll)} onChange={(e) => (setShowAll as (boolbase:boolean)=>void)(Boolean(e.target.checked))} />} label={t("content.showNodeAllProperties")} />
                </Box>
                {keys.map(key => {
                    return (
                        <Box key={key} sx={{ width: "100%", wordBreak:"break-all" }}>
                            <FormControlLabel
                                control={<Checkbox checked={data.javaData.selectedProperties.includes(key as keyof IA11yNode)} onChange={(e) => {
                                    if (e.target.checked) {
                                        data.javaData.selectedProperties.push(key as keyof IA11yNode)
                                    } else {
                                        data.javaData.selectedProperties = data.javaData.selectedProperties.filter(k => k !== key)
                                    }
                                }} />}
                                label={`${key}::${JSON.stringify(node[key as keyof IA11yNode])}`}
                            />
                        </Box>
                    )
                })}
            </Box>
        </>
    )
}
function WaitUI({ data }: { data: IActionA11yNodeExecute }) {
    if (!data.javaData.wait) {
        data.javaData.wait = {
            time: 1000,
            interval: 20,
            invisible: false
        }
    }
    const { t } = useTranslation("droid-project-interface/actions/a11y/node-execute/content")
    const [time] = useProxyWatch<IActionA11yNodeExecute, number>(data, 'javaData.wait.time', data.javaData.wait.time || 1000);
    const [interval] = useProxyWatch<IActionA11yNodeExecute, number>(data, 'javaData.wait.interval', data.javaData.wait.interval)
    const [invisible] = useProxyWatch<IActionA11yNodeExecute, boolean>(data, "javaData.wait.invisible", data.javaData.wait.invisible)
    return (
        <>
            <Stack direction="column" sx={{ width: "100%" }} spacing={2}>
                <TextField type="number" value={parseInt(time.toString())} onChange={(e) => data.javaData.wait.time = parseInt(e.target.value)} label={t("content.wait.time")} />
                <TextField type="number" value={parseInt(interval.toString())} onChange={(e) => data.javaData.wait.interval = parseInt(e.target.value)} label={t("content.wait.interval")} />
                <FormControlLabel control={<Checkbox checked={invisible} onChange={(e) => data.javaData.wait.invisible = e.target.checked} />} label={t("content.wait.invisible")} />
            </Stack>
        </>
    )
}
export function UI(data: IActionA11yNodeExecute) {
    const onInfoSelectedEventType = 'onInfoSelected';
    const { t } = useTranslation("droid-project-interface/actions/a11y/node-execute/content")
    const router = useRouter()
    const backPath = router.pathname;
    const [nodeAction, _setNodeAction] = useState(data.javaData.action)
    function setNodeAction(nodeAction: NodeAction) {
        _setNodeAction(nodeAction)
        data.javaData.action = nodeAction
    }
    const [text, _setText] = useState(data.javaData.setText)
    function setText(text: string) {
        _setText(text)
        data.javaData.setText = text
    }
    const [open, setOpen] = useProjectUniqueKeyValue<boolean>((projectInfo) => {
        childToProjectDataUtils.actionList.getActionListByProp(data, projectInfo)
        projectInfo.action = data
    }, 'open-accordion', false);
    function onChangeClick() {
        const projectInfo: IChildToProjectData = {}
        childToProjectDataUtils.actionList.getActionListByProp(data, projectInfo)
        if (!projectInfo.project?.id || !projectInfo.triggerActionGroup?.id || !projectInfo.triggerAction?.id || !projectInfo.actionGroup?.id) {
            console.error('UpdateAppLink projectInfo is undefined', projectInfo)
            return
        }

        const query: IActionOrTriggerListPageParams = {
            backPath,
            projectId: projectInfo.project?.id,
            triggerActionGroupId: projectInfo.triggerActionGroup?.id,
            triggerActionId: projectInfo.triggerAction?.id,
            actionGroupId: projectInfo.actionGroup?.id,
            type: ActionOrTriggerListTypeEnum.updateAction,
            actionId: data.id,
            actionType: actionA11yNodeExecuteUtils.type,
        }
        actionA11yNodeExecuteUtils.onClickChange?.({ query, onDataSelectedEventType: onInfoSelectedEventType })
    }
    useEffect(()=>{
        return getProxyDispatcher(data).addListener(onInfoSelectedEventType, (info:IA11yNodeInfo)=>{
            data.javaData.info = info
        })
    })
    if (!nodeAction) {
        return (
            <>
                <Box>
                    no node action!!!
                </Box>
            </>
        )
    }
    return (
        <>
            <Box sx={{ width: "100%" }}>
                <Accordion expanded={Boolean(open)} onChange={(e, expanded) => setOpen(Boolean(expanded))}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}>
                        <span>{t("content.title")}</span>
                    </AccordionSummary>
                    <AccordionDetails>

                        <Grid2 container sx={{ width: "100%" }}>
                            <Grid2 size="grow" container>
                                <Stack direction="column" spacing={2}>
                                    <NodeActionUI nodeAction={nodeAction} setNodeAction={setNodeAction} text={text || ""} setText={setText} />
                                    <WaitUI data={data} />
                                    <SelectConditions data={data} />
                                </Stack>
                            </Grid2>
                            <Grid2 size={"auto"}>
                                <Button onClick={onChangeClick}>
                                    <span>{t("content.changeBtnLabel")}</span>
                                </Button>
                            </Grid2>
                        </Grid2>
                    </AccordionDetails>
                </Accordion>
            </Box>
        </>
    )
}